/**
 * The ONE backend seam for the Ask Pine widget.
 *
 * Opens a WebSocket to Pine's own Node backend, which relays it to the Python
 * agent service (ai-ml). Sends one query and maps the event frames onto
 * callbacks. Everything else in the widget is presentation.
 *
 * The browser does not hold ai-ml's API key and does not know its address. It
 * presents a short-lived ticket instead, which Node resolves in process; ai-ml
 * is reachable only from Node. See backend/services/agentProxy.js.
 *
 * Frames are relayed unchanged, so the list below is still ai-ml's own.
 *
 * Server frames (ai-ml/api/routes.py):
 *   { event: 'classified',        data: { category } }
 *   { event: 'generating_sql',    data: {} }
 *   { event: 'sql_ready',         data: { sql, explanation } }
 *   { event: 'executing_query',   data: {} }
 *   { event: 'summarising',       data: {} }
 *   { event: 'researching',       data: {} }
 *   { event: 'searching_web',     data: { queries: [...] } }
 *   { event: 'research_retry',    data: { attempt } }
 *   { event: 'synthesising',      data: {} }
 *   { event: 'categorising',      data: {} }
 *   { event: 'answering',         data: {} }
 *   { event: 'qa_complete',       data: { confidence } }
 *   { event: 'thinking',          data: {} }
 *   { event: 'complete',          data: { answer, category, data, graph, qa, elapsed_seconds } }
 *   { event: 'error',             data: { message } }
 *
 * NOTE: the server returns the whole answer in `complete` — there is no token
 * streaming yet. `onAnswer` receives the full payload at once.
 */

import { issueTicket } from "./chatStore";

// Pine's own backend, not ai-ml. Port 7000 serves these alongside every other
// Pine route; the WebSocket arrives as an upgrade that Express never sees.
export const AGENT_WS_URL =
  process.env.REACT_APP_AGENT_WS_URL || "ws://localhost:7000/api/agent/ws";
export const AGENT_CLEAR_URL =
  process.env.REACT_APP_AGENT_CLEAR_URL || "ws://localhost:7000/api/agent/ws/clear";

// REACT_APP_AGENT_API_KEY is gone on purpose. It was a single shared key for
// every user, compiled into the bundle and sent in the query string, so it was
// readable in devtools and written into access logs. ai-ml's key now exists
// only in Node's environment.

// Derived from the WebSocket URL so there is one thing to configure at deploy
// time, not two that can drift apart.
export const AGENT_TRANSCRIBE_URL =
  process.env.REACT_APP_AGENT_TRANSCRIBE_URL ||
  AGENT_WS_URL.replace(/^ws/, "http").replace(/\/ws$/, "/transcribe");

/**
 * Send a recorded clip to Pine's backend, which relays it to ai-ml, and get
 * text back.
 *
 * Authenticated with the Pine JWT rather than a ticket: this is an ordinary
 * request and can carry an Authorization header. Tickets exist only because a
 * WebSocket handshake cannot.
 *
 * Throws on failure - the caller shows the message and the user types instead.
 */
export async function transcribeAudio(blob, token) {
  const form = new FormData();
  // The extension only has to match the blob's real type; the service does not
  // convert anything. Chromium records webm, iOS records mp4.
  const ext = (blob.type || "").includes("mp4") ? "m4a" : "webm";
  form.append("audio", blob, `clip.${ext}`);

  const res = await fetch(AGENT_TRANSCRIBE_URL, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });

  if (!res.ok) {
    let detail = "Could not transcribe that.";
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      /* keep the default */
    }
    throw new Error(detail);
  }

  const data = await res.json();
  return (data?.text || "").trim();
}

/** Human labels for each pipeline event, shown in the tool card. */
export const STEP_LABELS = {
  classified: "classify",
  generating_sql: "generate_sql",
  sql_ready: "sql_ready",
  executing_query: "execute_query",
  summarising: "summarise",
  researching: "research",
  searching_web: "search_web",
  research_retry: "retry_search",
  research_complete: "research_done",
  synthesising: "synthesise",
  categorising: "route_category",
  preparing_search: "preparing_search",
  preparing_lookup: "preparing_lookup",
  opening_search: "opening_search",
  needs_choice: "needs_choice",
  answering: "answer_from_kb",
  qa_complete: "kb_done",
  thinking: "think",
};

function stepDetail(event, d = {}) {
  if (event === "classified") return d.category || "";
  if (event === "searching_web") return (d.queries || []).join(" · ");
  if (event === "sql_ready") return d.explanation || "";
  if (event === "research_retry") return `attempt ${d.attempt}`;
  if (event === "qa_complete") return d.confidence ? `confidence: ${d.confidence}` : "";
  if (event === "generating_sql" && d.correcting) return `correcting, attempt ${d.attempt}`;
  return "";
}

export function newSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * Run one agent turn.
 *
 * @param {string} text            the user's message
 * @param {object} opts
 * @param {string} opts.sessionId  stable per chat thread — keeps server-side agent state
 * @param {Array}  opts.history    [{role, content}] prior turns
 * @param {string} opts.model      model id from the picker
 * @param {string} [opts.ticket]     opaque permission ticket from Pine's backend
 * @param {string} [opts.userName]   display name, for greeting and phrasing only.
 *                                   Comes from localStorage and is user-editable,
 *                                   so it must never drive a permission decision.
 * @param {string} [opts.forceRoute] skip classification and run this route —
 *                                   set when the user picks from a search
 *                                   disambiguation prompt
 * @param {(step)=>void}    opts.onStep    called per pipeline event
 * @param {(payload)=>void} opts.onAnswer  called once with the `complete` payload
 * @param {(msg)=>void}     opts.onError
 * @returns {{promise: Promise<void>, cancel: () => void}}
 */
export function callAgent(
  text,
  {
    sessionId,
    history = [],
    model,
    forceRoute = null,
    ticket = null,
    userName = null,
    onStep,
    onAnswer,
    onError,
  },
) {
  let ws;
  let settled = false;

  const promise = new Promise((resolve) => {
    // The ticket authenticates the connection to Node, and only that. Node
    // resolves it here, at the handshake, and tells ai-ml who this is.
    const url =
      `${AGENT_WS_URL}?ticket=${encodeURIComponent(ticket || "")}` +
      `&session_id=${encodeURIComponent(sessionId)}`

    try {
      ws = new WebSocket(url);
    } catch (e) {
      onError?.(`Cannot open WebSocket: ${e.message}`);
      return resolve();
    }

    const t0 = performance.now();
    const finish = () => {
      if (settled) return;
      settled = true;
      try {
        ws.close();
      } catch {
        /* already closed */
      }
      resolve();
    };

    // The ticket is NOT sent in the payload. It authenticates the handshake in
    // the URL above, and Pine's backend resolves it there and stamps the
    // caller's identity onto this message before ai-ml sees it. Sending it here
    // as well would put a credential in a frame that devtools displays, for no
    // benefit - the backend strips anything this payload claims regardless.
    ws.onopen = () =>
      ws.send(
        JSON.stringify({
          query: text,
          conversation_history: history,
          model,
          force_route: forceRoute,
          user_name: userName,
        }),
      );

    ws.onmessage = (ev) => {
      let frame;
      try {
        frame = JSON.parse(ev.data);
      } catch {
        return;
      }
      const { event, data = {} } = frame;

      if (event === "error") {
        onError?.(data.message || "Unknown error from the agent");
        return finish();
      }
      if (event === "complete") {
        onAnswer?.(data);
        return finish();
      }

      onStep?.({
        key: event,
        label: STEP_LABELS[event] || event,
        detail: stepDetail(event, data),
        sql: data.sql || null,
        ms: Math.round(performance.now() - t0),
      });
    };

    ws.onerror = () => {
      // Deliberately does not name ai-ml. The browser talks only to Pine's
      // backend now and cannot tell which of the two is down, or whether the
      // ticket was simply refused.
      onError?.("Could not reach the Ask Pine service. Please try again.");
      finish();
    };
    ws.onclose = () => finish();
  });

  return {
    promise,
    cancel: () => {
      settled = true;
      try {
        ws?.close();
      } catch {
        /* noop */
      }
    },
  };
}

/**
 * Drop the server-side agent for a session so the next thread starts clean.
 *
 * Takes the Pine JWT and fetches its own ticket, rather than being handed one,
 * so the caller stays synchronous - newThread does not await this and never
 * did. Fire and forget: if it fails the old agent simply expires on its own.
 */
export async function clearAgentSession(sessionId, token) {
  if (!sessionId || !token) return;
  try {
    const ticket = await issueTicket(token);
    if (!ticket) return;

    const ws = new WebSocket(
      `${AGENT_CLEAR_URL}?ticket=${encodeURIComponent(ticket)}` +
        `&session_id=${encodeURIComponent(sessionId)}`
    );
    ws.onopen = () => setTimeout(() => { try { ws.close(); } catch { /* noop */ } }, 200);
  } catch {
    /* non-critical — the server makes a fresh agent anyway */
  }
}
